import { datadogRum } from "@datadog/browser-rum";
import {
  type AnalyticsClient,
  type AnalyticsEventKey,
  type AnalyticsEventProperties,
  analyticsEventGoesTo,
  resolveDatadogAction,
} from "@healthos/analytics";
import { clearDatadogUser, isDatadogRumEnabled, setDatadogUser } from "./datadog";
import { isInDesktopEmbed } from "./host-auth";

/**
 * Datadog RUM adapter for the shared event contract.
 *
 * Every tracked event becomes a RUM custom action named after its catalog key,
 * which is what makes a Datadog funnel buildable: funnel steps are picked by
 * action name, and catalog keys are unique where Segment event names
 * deliberately are not. Properties ride along as the action context, so each
 * one is a facet a funnel can be split or filtered by.
 *
 * This sink adds no attribute of its own beyond the surface. The contract is
 * PHI-safe by construction and stays that way here.
 */
export const datadogAnalytics: AnalyticsClient = {
  identify(userId) {
    // Funnels count distinct users, not sessions, so a funnel is only as good
    // as this call. App-level auth sets the same id; setting it again is
    // idempotent and keeps the sink correct on its own. Through the helper
    // rather than datadogRum.setUser, which would replace the whole usr object
    // and drop the company tag.
    setDatadogUser(userId);
  },
  reset() {
    clearDatadogUser();
  },
  page(screenName) {
    if (!isDatadogRumEnabled()) return;
    // The React plugin already tracks a view per route. This names the screen
    // in our own vocabulary so a funnel mixing views and actions can group on
    // one attribute rather than parsing URLs.
    datadogRum.setGlobalContextProperty("screen_name", screenName);
  },
  track<Key extends AnalyticsEventKey>(key: Key, properties: AnalyticsEventProperties[Key]) {
    if (!isDatadogRumEnabled() || !analyticsEventGoesTo(key, "datadog")) return;
    const action = resolveDatadogAction(key, properties);
    datadogRum.addAction(action.name, {
      ...action.context,
      client_surface: isInDesktopEmbed() ? "desktop" : "web",
    });
  },
};
